import { useQuery } from 'react-query'
import {
  diffCatalogues,
  shapeFromManifest,
} from '../../shared/catalogue-diff'
import type { Change } from '../../shared/catalogue-diff'
import type { Baseline } from '../../shared/publish-view'
import { shouldRetry } from '../../shared/retry'
import type { AvatarBoClient, BoCatalog } from '../../shared/types'
import { catalogueFromVersion } from '../../shared/version-preview'

export interface UseVersionChangesOpts {
  client: AvatarBoClient
  setId?: string
  /** The version being inspected; undefined while the dialog is closed. */
  version?: number
  baseline?: Baseline
}

export interface VersionChanges {
  changes?: Change[]
  /** The version rebuilt as a catalogue, once its artwork URLs have arrived. */
  catalogue?: BoCatalog
  isLoading: boolean
  error?: string
}

const key = (kind: string, n: number) => ['avatar-bo', kind, n] as const

/**
 * What one version changed, and the same version reassembled for the try-on preview.
 *
 * The manifest is the truth for structure and tiers; the config document is fetched ONLY for its
 * resolved artwork URLs, which the manifest deliberately does not carry.
 */
export function useVersionChanges({
  client,
  setId,
  version,
  baseline,
}: UseVersionChangesOpts): VersionChanges {
  const enabled = version !== undefined && Boolean(setId)
  const opts = { refetchOnWindowFocus: false, retry: shouldRetry }

  const target = useQuery(
    key('manifest', version ?? 0),
    () => client.getVersionManifest(version as number),
    { ...opts, enabled },
  )
  const base = useQuery(
    key('manifest', baseline?.n ?? 0),
    () => client.getVersionManifest(baseline?.n as number),
    { ...opts, enabled: enabled && baseline !== undefined },
  )
  const config = useQuery(
    key('config', version ?? 0),
    () => client.getVersionConfig(setId as string, version as number),
    { ...opts, enabled },
  )

  if (!enabled) {
    return { isLoading: false }
  }

  const failed = [target, base, config].find((q) => q.isError)
  if (failed) {
    return {
      isLoading: false,
      error:
        failed.error instanceof Error ? failed.error.message : 'request failed',
    }
  }

  const baseShape =
    baseline === undefined
      ? { sections: [] }
      : base.data && setId
        ? shapeFromManifest(base.data, setId)
        : undefined
  const changes =
    target.data && baseShape && setId
      ? diffCatalogues(baseShape, shapeFromManifest(target.data, setId)).changes
      : undefined

  return {
    changes,
    catalogue:
      target.data && config.data && setId
        ? catalogueFromVersion(target.data, config.data, setId) ?? undefined
        : undefined,
    isLoading: changes === undefined,
  }
}
